// VER-218 / productization: shell for the Console. Sider navigation + header
// (current user + logout), body renders the active screen via <Outlet/>.
// Role-aware: every authenticated user sees the client dashboard (Explore); admins
// additionally see Management; internal_admin also sees the Internal group. RBAC is
// enforced per-route in App.jsx — this nav only hides.
import { useState, useEffect } from "react";
import { Layout, Menu, Typography, Button, Space, Tag, Segmented, Tooltip } from "antd";
import {
  HomeOutlined,
  FileTextOutlined,
  ApiOutlined,
  TeamOutlined,
  MailOutlined,
  SafetyOutlined,
  FileSearchOutlined,
  BankOutlined,
  TagsOutlined,
  DatabaseOutlined,
  ThunderboltOutlined,
  CloudServerOutlined,
  DeleteOutlined,
  LogoutOutlined,
} from "@ant-design/icons";
import { useLocation, useNavigate, Outlet } from "react-router-dom";
import ApiDocsNav from "./ApiDocsNav";
import { useSession } from "../../console/useSession";
import { EnvContext } from "../../console/useEnv";
import { InsideConsoleContext } from "../../console/insideConsole";
import { getEnv, setEnv as persistEnv } from "../../console/envStore";
import { ROLES, ADMIN_ROLES, roleLabel } from "../../console/consoleUtils";

const { Sider, Header, Content } = Layout;
const { Title, Text } = Typography;

export default function ConsoleLayout() {
  const { user, logout } = useSession();
  const navigate = useNavigate();
  const location = useLocation();

  // Brand shown in the shell — the account's brand display name (e.g. "Vaultro").
  // The backend always resolves one (defaulting to VerifyU), so no fallback here.
  const brandName = user?.brand?.displayName;

  // Reflect the brand in the browser tab title once we know it.
  useEffect(() => {
    if (brandName) document.title = `${brandName} Console`;
  }, [brandName]);

  const isInternal = user?.role === ROLES.INTERNAL_ADMIN;
  // Account admins manage their OWN org via the Management group. Internal admins manage
  // EVERY org (including VerifyU's own) by drilling into it from Accounts — so they don't
  // get a global Management group (that's what let an invite land in the wrong company).
  const isAccountAdmin = ADMIN_ROLES.includes(user?.role) && !isInternal;

  // Environment ("mode") toggle — Stripe test/live style. Identity is mode-independent;
  // env-scoped screens (My APIs, credits, usage) re-fetch from the selected data plane.
  const [env, setEnvState] = useState(getEnv());
  const changeEnv = (value) => setEnvState(persistEnv(value));

  // Contextual left column: on API Docs the sidebar becomes the endpoint navigator
  // (single, consistent sidebar). "‹ Menu" reveals the normal nav in place without
  // navigating away; leaving/re-entering Docs resets it.
  const onDocs = location.pathname.startsWith("/console/docs");
  const [showMenuOnDocs, setShowMenuOnDocs] = useState(false);
  useEffect(() => {
    setShowMenuOnDocs(false);
  }, [location.pathname]);
  const showEndpointNav = onDocs && !showMenuOnDocs;

  const items = [
    // Overview / landing.
    { key: "/console", icon: <HomeOutlined />, label: "Home" },
    // Client dashboard — every authenticated user.
    {
      type: "group",
      label: "Explore",
      children: [
        { key: "/console/docs", icon: <FileTextOutlined />, label: "API Docs" },
        { key: "/console/my-apis", icon: <ApiOutlined />, label: "My APIs" },
      ],
    },
    // Own-org management — account admins only. (Internal admins do this per-company
    // via Accounts drill-in.)
    ...(isAccountAdmin
      ? [
          {
            type: "group",
            label: "Management",
            children: [
              { key: "/console/users", icon: <TeamOutlined />, label: "Users" },
              { key: "/console/invites", icon: <MailOutlined />, label: "Invites" },
              { key: "/console/account", icon: <SafetyOutlined />, label: "Account" },
              { key: "/console/audit-log", icon: <FileSearchOutlined />, label: "Audit log" },
            ],
          },
        ]
      : []),
    // VerifyU Ops only — client tenants (drill in to manage people), audit, and tools.
    ...(isInternal
      ? [
          {
            type: "group",
            label: "Internal",
            children: [
              { key: "/console/accounts", icon: <BankOutlined />, label: "Accounts" },
              { key: "/console/brands", icon: <TagsOutlined />, label: "Brands" },
              { key: "/console/audit-log", icon: <FileSearchOutlined />, label: "Audit log" },
              {
                key: "/console/tools/api-collection",
                icon: <DatabaseOutlined />,
                label: "API Collection",
              },
              {
                key: "/console/tools/query-builder",
                icon: <ThunderboltOutlined />,
                label: "Query Builder",
              },
              {
                key: "/console/tools/s3-files",
                icon: <CloudServerOutlined />,
                label: "S3 Files",
              },
              {
                key: "/console/tools/rc-delete",
                icon: <DeleteOutlined />,
                label: "RC Delete",
              },
            ],
          },
        ]
      : []),
  ];

  // Flatten leaf keys, then highlight the active one. Match on an exact path or a real
  // path segment ("/console/account" must NOT match "/console/accounts"). The Home key
  // "/console" matches ONLY the exact path — otherwise it would swallow every sub-route.
  const leafKeys = items.flatMap((g) => (g.children ? g.children.map((c) => c.key) : [g.key]));
  const selectedKey =
    leafKeys.find(
      (k) =>
        location.pathname === k ||
        (k !== "/console" && location.pathname.startsWith(k + "/"))
    ) || "/console";

  const handleLogout = async () => {
    await logout();
    navigate("/console/login", { replace: true });
  };

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <Sider
        breakpoint="lg"
        collapsedWidth="0"
        theme="light"
        width={showEndpointNav ? 280 : 220}
        style={{ height: "100vh", position: "sticky", top: 0 }}
      >
        <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
          <div style={{ padding: "20px 16px 8px" }}>
            <Title level={4} style={{ margin: 0 }}>
              {brandName} Console
            </Title>
          </div>
          <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflowY: "auto" }}>
            {showEndpointNav ? (
              <ApiDocsNav onBack={() => setShowMenuOnDocs(true)} />
            ) : (
              <Menu
                mode="inline"
                selectedKeys={[selectedKey]}
                items={items}
                onClick={({ key }) => {
                  // Returning to Docs from the revealed menu shows the endpoint nav again.
                  if (key.startsWith("/console/docs")) setShowMenuOnDocs(false);
                  navigate(key);
                }}
                style={{ borderInlineEnd: "none" }}
              />
            )}
          </div>
        </div>
      </Sider>

      <Layout>
        <Header
          style={{
            background: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 16,
            paddingInline: 24,
            borderBottom: "1px solid #f0f0f0",
          }}
        >
          <Space size="middle">
            <Tooltip title="Which environment's data you're viewing. Your login is the same in both.">
              <Segmented
                size="small"
                value={env}
                onChange={changeEnv}
                options={[
                  { label: "Preprod", value: "preprod" },
                  { label: "Prod", value: "prod" },
                ]}
              />
            </Tooltip>
            <Space size={8}>
              <Text>{user?.email}</Text>
              {user?.role && (
                <Tag color={isInternal ? "geekblue" : "default"}>{roleLabel(user.role)}</Tag>
              )}
            </Space>
            <Button icon={<LogoutOutlined />} onClick={handleLogout}>
              Log out
            </Button>
          </Space>
        </Header>

        <Content style={{ margin: 24 }}>
          {/* Env-scoped screens read the current mode from here and re-fetch on change.
              InsideConsoleContext tells reused legacy screens to skip their own shell. */}
          <InsideConsoleContext.Provider value={true}>
            <EnvContext.Provider value={{ env, setEnv: changeEnv }}>
              <Outlet />
            </EnvContext.Provider>
          </InsideConsoleContext.Provider>
        </Content>
      </Layout>
    </Layout>
  );
}
